import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import { Breadcrumbs, Link, Typography, Stepper, Step, StepLabel, Button } from '@mui/material';
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { PRODUCT_OPTIONS } from "../constants/productsOptions";
import UserProductsRegistrationBulkUpload from '../components/UserProductsRegistrationBulkUpload';

export default function UserProductRegistrationBulk() {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);

    const compId = queryParams.get('compId');
    const compName = queryParams.get('compName');

    let companyType = decodeURIComponent(queryParams.get('companytype') || "");

    if (companyType !== "Ingredient Manufacturer" && companyType !== "Ingredient Distributor") {
        companyType = "Ingredient Manufacturer";
    }

    const steps = [
        "Download Template",
        "Add your product data",
        "Upload Excel file",
        "Submit to register products"
    ];

    const downloadTemplate = async () => {
        const workbook = new ExcelJS.Workbook();

        const mainSheet = workbook.addWorksheet("Products");
        const optionSheet = workbook.addWorksheet("Options");

        // Hide options sheet
        optionSheet.state = "hidden";

        // ✅ Prepare values
        const currencyValues = PRODUCT_OPTIONS.currencies.map(c => c.value);

        // Helper to fill column
        const fillColumn = (col, values) => {
            values.forEach((val, i) => {
                optionSheet.getCell(`${col}${i + 1}`).value = val;
            });
        };

        fillColumn("A", PRODUCT_OPTIONS.categories);
        fillColumn("B", currencyValues);
        fillColumn("C", PRODUCT_OPTIONS.productTypes);
        fillColumn("D", PRODUCT_OPTIONS.formats);
        fillColumn("E", PRODUCT_OPTIONS.units);

        // Main sheet columns
        mainSheet.columns = [
            { header: "Product Name", key: "name", width: 25 }, //A
            { header: "Description", key: "desc", width: 30 }, //B
            { header: "Technical Details", key: "tech_dtls", width: 30 }, //C
            { header: "Concentration %", key: "concentration", width: 30 }, //D
            { header: "Source", key: "source", width: 30 }, //F
            { header: "Category", key: "category", width: 25 }, //G --
            { header: "Price", key: "price", width: 15 }, //H
            { header: "Currency", key: "currency", width: 15 }, //I --
            { header: "MOQ", key: "moq", width: 10 }, //J
            { header: "Unit", key: "unit", width: 10 }, //M --
            { header: "Product Type", key: "type", width: 20 }, //K --
            { header: "Formats available", key: "formats", width: 25 }, //L --
            { header: "Features", key: "features", width: 30 }, //N
            { header: "Grades", key: "grades", width: 20 }, //O
        ];

        // Apply dropdown using reference (SAFE ✅)
        const applyDropdown = (col, formula) => {
            for (let i = 2; i <= 200; i++) {
                mainSheet.getCell(`${col}${i}`).dataValidation = {
                    type: "list",
                    allowBlank: true,
                    formulae: [formula]
                };
            }
        };

        applyDropdown("F", "Options!$A$1:$A$50"); // Category
        applyDropdown("H", "Options!$B$1:$B$10"); // Currency
        applyDropdown("K", "Options!$C$1:$C$10"); // Product Type
        applyDropdown("L", "Options!$D$1:$D$20"); // Formats
        applyDropdown("J", "Options!$E$1:$E$10"); // Unit

        mainSheet.getRow(1).font = { bold: true };

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), "bulk_products_template.xlsx");
    };

    return (
        <Wrapper className='container'>
            <Breadcrumbs className='mb-5 mt-2 pageBreadCrumbs'>
                <Link href="/user-companies"> Companies </Link>
                <Link href={`/user-company-details?compId=${compId}`}> {compName} </Link>
                <Typography> Bulk Register Ingredients </Typography>
            </Breadcrumbs>

            <h2 className='pageTitle after_title_line'><span>Bulk Register Ingredients</span></h2>

            <div className='statusBox p-3'>
                <p>
                    Register Ingredients for <strong>{compName}</strong> {companyType}
                </p>

                <div className="mb-4">
                    <div className='stepsWrapper'>
                        <Stepper activeStep={-1} alternativeLabel>
                            {steps.map((label) => (
                                <Step key={label}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </div>

                    <div className="mt-4 text-center">
                        <Button variant="contained" onClick={downloadTemplate}>
                            Download Template
                        </Button>
                    </div>
                </div>
            </div>

            <UserProductsRegistrationBulkUpload
                compId={compId}
                compName={compName}
                companyType={companyType}
            />
        </Wrapper>
    );
}

const Wrapper = styled.div`
.statusBox {
    background: var(--bg-color);
    border-left: 6px solid var(--secondary-color);
    padding: 10px;
    margin-bottom: 20px;
}
.pageBreadCrumbs{ 
    a:hover { 
        text-decoration: none; 
        color: var(--secondary-color);
    }
}
.stepsWrapper {
    width: 850px;
}
`;